/**
 * Blog listing pager: sliding window of page numbers on narrow containers.
 *
 * The "Blog Listing" ACF block (inc/blocks/blog-listing.php) prints every
 * page number from 1 to max_num_pages with no limit or ellipsis. On a
 * container narrower than the full row this shows only a window of numbers
 * that fits on one line, anchored at page 1 by default (numbers hide from
 * the right, just before the "next" arrow — same as before).
 *
 * Once the current page reaches the last number in that window, the window
 * slides forward instead of just truncating further: it keeps up to 2 pages
 * ahead of the current one visible, hiding numbers from the left as needed.
 * Example: 9 pages, 5 fit. Landing on page 5 (the last one shown) moves the
 * window from [1..5] to [3..7] — 1 and 2 disappear on the left, 6 and 7
 * appear on the right.
 *
 * Every navigation here is a real page load (real `?blist_page=N` links, no
 * AJAX), so there is no previous window to remember between clicks — the
 * window is recomputed from scratch on each load from (current page,
 * capacity, total pages). `.is-fitted` (src/sass/blocks/_blog-listing.scss)
 * is what switches the row from wrapping to a single measured line; without
 * it the row just wraps like before, so a JS failure degrades safely.
 *
 * No optional chaining (?.) or nullish coalescing (??) — gulp-babel has no
 * preset configured, so uglify-js parses this as written.
 */
(function () {
  const LOOKAHEAD = 2;
  const pagers = document.querySelectorAll(".blist-pager");
  if (!pagers.length) return;
  function fit(nav) {
    const numsWrap = nav.querySelector(".blist-pager-nums");
    if (!numsWrap) return;
    const items = numsWrap.querySelectorAll(".blist-pg-item");
    const total = items.length;
    if (!total) return;
    function applyWindow(start, end) {
      for (let i = 0; i < total; i++) {
        const pageNum = i + 1;
        if (pageNum >= start && pageNum <= end) {
          items[i].classList.remove("is-collapsed");
        } else {
          items[i].classList.add("is-collapsed");
        }
      }
    }

    // Show every number first, so the row can grow back once the container
    // has more room again, and so the fit check below reflects the truth.
    applyWindow(1, total);
    if (numsWrap.scrollWidth <= numsWrap.clientWidth) return; // all fit, nothing to hide

    // Numbers are fixed-width (44px min-width, centered text), so how many
    // fit doesn't depend on which ones are shown — measure it once with a
    // window anchored at 1.
    let capacity = total;
    for (; capacity > 1; capacity--) {
      applyWindow(1, capacity);
      if (numsWrap.scrollWidth <= numsWrap.clientWidth) break;
    }
    let currentPage = 1;
    for (let i = 0; i < total; i++) {
      if (items[i].classList.contains("is-on")) {
        currentPage = i + 1;
        break;
      }
    }

    // Default window is [1, capacity]. Only slide it once the current page
    // reaches its far edge, then keep up to LOOKAHEAD pages ahead visible.
    const end = currentPage < capacity ? capacity : Math.min(total, currentPage + LOOKAHEAD);
    const start = Math.max(1, end - capacity + 1);
    applyWindow(start, end);
  }
  function fitAll() {
    for (let i = 0; i < pagers.length; i++) {
      fit(pagers[i]);
    }
  }
  for (let i = 0; i < pagers.length; i++) {
    pagers[i].classList.add("is-fitted");
  }
  fitAll();
  if ("ResizeObserver" in window) {
    const ro = new ResizeObserver(fitAll);
    for (let i = 0; i < pagers.length; i++) {
      ro.observe(pagers[i]);
    }
  } else {
    window.addEventListener("resize", fitAll);
  }
})();